/**
 * In-app feedback / bug submission API client (#504, D4-FBK-001..010).
 *
 * Wraps `POST /api/v1/feedback/`. The browser sends only NON-AUTHORITATIVE
 * client hints (route, app version, viewport, locale, breadcrumb, captured
 * console errors, a `type #id` record reference). The server overrides/derives
 * the authoritative fields (reporter identity/role, agency, instance, host, UA)
 * and creates the GitHub issue server-side -- the GitHub token never reaches
 * the browser. Screenshot / screen-recording is sent as multipart `media`.
 */
import {
  getAccessToken,
  notifySessionExpired,
  refreshSession,
  responseIsAuthExpiry,
} from "./client";
import type { BrightmarkAnnotation } from "../lib/brightmark/types";

const API_BASE = "/api/v1";

/**
 * Refresh-aware authed fetch for the feedback endpoints (#1482).
 *
 * The feedback READ calls (reports list / detail / sync / close-out) used to
 * attach `getAccessToken()` and `fetch()` exactly once, with NO 401 handling --
 * so a short-lived (15 min) access token that expired while the page sat open
 * (or after an `lwp-api` restart) surfaced a raw "Could not load feedback
 * reports (401)" banner instead of silently refreshing. This helper gives every
 * feedback call the same proven refresh-once-and-retry that `submitFeedback`
 * already had (#883): attach token -> on an auth-expiry (401 or a 403 DRF
 * NotAuthenticated, #2376) `refreshSession()` (mint a fresh access token from the
 * HttpOnly refresh cookie, #972) -> retry the SAME request once -> if still an
 * auth-expiry (or the refresh itself failed) run the friendly session-expired
 * flow (`notifySessionExpired()` -> AuthProvider re-login) and throw the friendly
 * message, never the verbatim SimpleJWT auth string.
 *
 * `doFetch(token)` MUST rebuild the request each call (fresh headers carrying
 * the given bearer token, and -- for multipart -- a body that survives a retry).
 * Returns the final `Response`; callers own non-auth status handling (404,
 * permission-403, !ok detail extraction) exactly as before.
 */
async function authedFeedbackFetch(
  doFetch: (token: string | null) => Promise<Response>,
): Promise<Response> {
  let resp = await doFetch(getAccessToken());
  // An auth-expiry is a 401 OR a 403 whose body is the DRF NotAuthenticated
  // shape (#2376) -- both are usually just a stale JWT. Anything else is the
  // caller's to handle (404, permission 403, !ok detail extraction) as before.
  if (!(await responseIsAuthExpiry(resp))) return resp;

  // Refresh once and retry the SAME request with the fresh token.
  const newAccess = await refreshSession();
  if (newAccess) {
    resp = await doFetch(newAccess);
    if (!(await responseIsAuthExpiry(resp))) return resp;
    // Still auth-expiry after a fresh token -- the session is genuinely gone.
    notifySessionExpired();
    throw new Error("Session expired. Please log in again.");
  }

  // Refresh failed (or no refresh cookie) -- friendly session-expired flow.
  notifySessionExpired();
  throw new Error("Session expired. Please log in again.");
}


/** The four friendly feedback tiles -> backend `type` values. */
export type FeedbackType = "bug" | "data_integrity" | "suggestion" | "feature";

export interface FeedbackClientHints {
  page_name?: string;
  viewport?: string;
  screen?: string;
  locale?: string;
  online?: boolean;
  platform?: string;
  breadcrumb?: string[];
  filters?: Record<string, unknown>;
  console_errors?: string[];
  last_api_call?: { method?: string; path?: string; status?: number };
}

export interface SubmitFeedbackInput {
  type: FeedbackType;
  /**
   * Optional explicit subject. The in-app modal no longer collects a Title
   * (#1052) -- the server derives the issue/record title from the description.
   * Only legacy/programmatic callers pass one; when omitted it is not sent.
   */
  title?: string;
  description: string;
  route?: string;
  app_version?: string;
  /** Record in context, by type + ID ONLY (POPIA), e.g. "deal #1423". */
  record_ref?: string;
  local_time?: string;
  client_hints?: FeedbackClientHints;
  /** Optional screenshot or screen recording. */
  media?: File | Blob | null;
  /**
   * Optional brightmark visual annotation (point-and-click / text-select). Serialised to the
   * multipart `annotation` field as a JSON string, matching the backend contract
   * (apps/feedback/annotation.py). Unknown keys are dropped server-side; submission still 201s.
   */
  annotation?: BrightmarkAnnotation | null;
}

export interface FeedbackResult {
  id: number;
  status: string;
  issue_number: number | null;
  /** The GitHub issue number, `null` until the issue is filed (#4507). */
  github_issue_number?: number | null;
  /** The CRM report number (the report's pk) -- always, since #4507. */
  reference: number;
  message: string;
}

/**
 * Submit feedback. Always sent as multipart/form-data so an optional
 * screenshot / screen recording can ride along; the JSON payload is carried in
 * a `payload` field plus top-level fields the serializer reads directly.
 */
export async function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<FeedbackResult> {
  const form = new FormData();
  form.append("type", input.type);
  // Title is optional now (#1052); send only when a caller supplies one.
  if (input.title && input.title.trim())
    form.append("title", input.title.trim());
  form.append("description", input.description);
  if (input.route) form.append("route", input.route);
  if (input.app_version) form.append("app_version", input.app_version);
  if (input.record_ref) form.append("record_ref", input.record_ref);
  if (input.local_time) form.append("local_time", input.local_time);
  if (input.client_hints)
    form.append("client_hints", JSON.stringify(input.client_hints));
  if (input.media) form.append("media", input.media);
  if (input.annotation)
    form.append("annotation", JSON.stringify(input.annotation));

  // POST the multipart form with the given bearer token (if any). The browser
  // sets the multipart boundary itself, so Content-Type is intentionally NOT
  // set -- forcing it (e.g. application/json) would break the upload.
  const postForm = (token: string | null) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/`, {
      method: "POST",
      headers,
      body: form,
    });
  };

  // Refresh-aware retry (#1482 helper, originally #883): a short-lived (15 min)
  // access token can expire while the modal sits open (typing / screen recording
  // / large upload), so a 401 here is usually just a stale JWT -- refresh once
  // and retry the SAME multipart POST (the helper rebuilds the headers with the
  // fresh token; the FormData body is reused untouched so the multipart boundary
  // the browser set is preserved), or run the friendly session-expired flow.
  const resp = await authedFeedbackFetch(postForm);

  if (!resp.ok) {
    let detail = `Could not submit feedback (${resp.status}).`;
    try {
      const data = await resp.json();
      detail =
        (data?.detail as string) ||
        (data?.title?.[0] as string) ||
        (data?.description?.[0] as string) ||
        detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return resp.json();
}

/** Best-effort, dependency-free capture of non-authoritative client hints. */
export function captureClientHints(): FeedbackClientHints {
  const hints: FeedbackClientHints = {};
  try {
    hints.viewport = `${window.innerWidth}x${window.innerHeight}`;
    hints.screen = `${window.screen?.width}x${window.screen?.height}`;
    hints.locale = navigator.language;
    hints.online = navigator.onLine;
    hints.platform = navigator.platform;
  } catch {
    /* non-fatal */
  }
  return hints;
}

// --- Reports list (D4-FBK-017) ----------------------------------------------

/** Backend status values for a feedback row. */
export type FeedbackStatus = "queued" | "submitted" | "mock" | "failed";

/**
 * A row in the feedback reports list. The backend is role-scoped: admins get
 * the full shape (GitHub linkage + submitter name); non-admins get only the
 * narrow fields (id, type, title, status, reference, created_at) -- no
 * `github_issue_url` or submitter identity is ever sent to a non-admin.
 */
export interface FeedbackReport {
  /**
   * Local Feedback pk when this row has a local record; `null` for a
   * cross-dashboard ledger-only row (raised on another dashboard, e.g. ops),
   * which has no local DB row. The detail modal degrades to the list-row fields
   * for a null-id row (no per-row detail fetch).
   */
  id: number | null;
  feedback_type: FeedbackType;
  title: string;
  status: FeedbackStatus;
  created_at: string;
  /**
   * Own-reports view only: the GitHub issue number once filed, else the CRM
   * report number (legacy shape, kept for compatibility). Display the CRM
   * number via `refLabel` and the GitHub number via `githubNumber` (#4507).
   */
  reference?: number;
  /**
   * Completion (close-out) layer (D4-FBK-018..021): both roles get `is_done`
   * (linked GitHub issue closed = done) + the authored `closeout_summary` /
   * `verify_url`. Admins additionally get the raw GitHub state fields.
   */
  is_done?: boolean;
  closeout_summary?: string;
  verify_url?: string;
  /**
   * Source dashboard (#1715, admin "all" view only). `source` is the
   * `agency:<slug>` slug; `source_label` is the friendly label ("LWP CRM",
   * "Ops dashboard", ...). Absent on the agent own-only view.
   */
  source?: string;
  source_label?: string;
  /** Admin-only fields. */
  github_issue_number?: number | null;
  github_issue_url?: string | null;
  submitted_by_name?: string;
  issue_state?: "" | "open" | "closed";
  issue_closed_at?: string | null;
  state_synced_at?: string | null;
  /**
   * Triage essentials (#3830) -- admin "all" view only. Empty strings when the
   * report has not been triaged yet.
   */
  risk?: FeedbackRisk;
  triage_status?: FeedbackTriageStatus;
  needs_authorisation?: boolean;
  requires_approval?: boolean;
  tags?: string[];
  triage_note?: string;
  /**
   * Root cause (D4-FBK-039, #4200) -- admin "all" view only; `""` until set.
   * Never present on the reporter's own-reports view.
   */
  root_cause?: FeedbackRootCause;
}

/**
 * Where a report really came from (D4-FBK-039, #4200). `""` = not set yet.
 * Admin-only: the reporter's own view never carries it.
 */
export type FeedbackRootCause =
  | ""
  | "legacy_data"
  | "how_to"
  | "crm_fault"
  | "change_request";

/** Triage risk level (#3830). Two levels only, low and high (#3871). */
export type FeedbackRisk = "" | "low" | "high";

/** Triage lifecycle status (#3830). */
export type FeedbackTriageStatus =
  | ""
  | "triaged"
  | "awaiting_approval"
  | "approved"
  | "declined"
  | "in_progress"
  | "resolved";

/** The admin detail's triage block (#3830). */
export interface FeedbackTriage {
  risk: FeedbackRisk;
  triage_status: FeedbackTriageStatus;
  tags: string[];
  area: string;
  needs_authorisation: boolean;
  requires_approval: boolean;
  triage_note: string;
  triaged_at: string | null;
  decision: "" | "approved" | "declined";
  decided_by_name: string;
  decided_at: string | null;
  decision_comment: string;
  /** Root cause (D4-FBK-039, #4200); `""` until set. */
  root_cause?: FeedbackRootCause;
}

/** Page-level completion roll-up over the full filtered queryset. */
export interface FeedbackCompletion {
  done: number;
  total: number;
}

export interface FeedbackReportPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: FeedbackReport[];
  /** X-of-N done roll-up (honours the active filters). */
  completion?: FeedbackCompletion;
  /**
   * The distinct Source (dashboard) options present in the admin "all" view
   * (#1715), each `{value: slug, label}`. Absent on the agent own-only view.
   */
  available_sources?: FeedbackSourceOption[];
  /** Whether THIS viewer may approve / decline (#3830, admin view only). */
  viewer_can_approve?: boolean;
}

/** Completion filter values for the list query. */
export type FeedbackCompletionFilter = "done" | "open" | "";

export interface FeedbackReportFilters {
  type?: FeedbackType | "";
  status?: FeedbackStatus | "";
  completion?: FeedbackCompletionFilter;
  /** Source (dashboard) slug — admin "all" view only (#1715). */
  source?: string;
  /** Triage status filter — admin "all" view only (#3830). */
  triage?: FeedbackTriageStatus;
  /** Root-cause filter -- admin "all" view only (#4200); `"none"` = not set. */
  cause?: Exclude<FeedbackRootCause, ""> | "none" | "";
  /**
   * Report-number search (#4507): the CRM report number or the GitHub issue
   * number ("601", "#601", "Report 601"). Scoped server-side to what the
   * viewer may already see.
   */
  number?: string;
  page?: number;
}

/** A Source (dashboard) option for the admin Source filter (#1715). */
export interface FeedbackSourceOption {
  value: string;
  label: string;
}

/**
 * Fetch the role-scoped reports list. The server decides visibility from the
 * caller's role -- this client just forwards the optional type/status filters
 * and page. Returns the DRF page-number envelope.
 */
export async function listFeedbackReports(
  filters: FeedbackReportFilters = {},
  signal?: AbortSignal,
): Promise<FeedbackReportPage> {
  const params = new URLSearchParams();
  if (filters.type) params.set("type", filters.type);
  if (filters.status) params.set("status", filters.status);
  if (filters.completion) params.set("completion", filters.completion);
  if (filters.source) params.set("source", filters.source);
  if (filters.triage) params.set("triage", filters.triage);
  if (filters.cause) params.set("cause", filters.cause);
  if (filters.number && filters.number.trim())
    params.set("number", filters.number.trim());
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  const qs = params.toString();

  // Refresh-aware (#1482): a stale 15-min access token (page left open / lwp-api
  // restart) silently refreshes and retries instead of surfacing a raw 401.
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/${qs ? `?${qs}` : ""}`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (!resp.ok) {
    // The status rides on the error so the list can tell "that page no longer
    // exists" (404 -- the set shrank under it) from a real failure (#4507).
    const err = new Error(
      `Could not load feedback reports (${resp.status}).`,
    ) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

// --- Report detail (#629): clickable row -> friendly detail view ------------

/**
 * The brightmark anchor the user pinned, as returned by the detail endpoint
 * (already PII-sanitised server-side). Every field is optional -- the client
 * renders whatever is present.
 */
export interface FeedbackAnchor {
  anchor_kind?: "element" | "text";
  selector_css?: string;
  selector_xpath?: string;
  element_html?: string;
  text_snippet?: string;
  quote_exact?: string;
  nearby_text?: string;
  source_file?: string;
}

/**
 * The engineering context, rendered behind the collapsed "Technical details"
 * disclosure. Only keys with a value are returned, so every field is optional.
 */
export interface FeedbackTechnical {
  app_version?: string;
  environment?: string;
  route?: string;
  page_name?: string;
  submitted_at_local?: string;
  record_ref?: string;
  user_agent?: string;
  viewport?: string;
  platform?: string;
  locale?: string;
  console_errors?: string[];
  annotation?: FeedbackAnchor;
}

/** A friendly screenshot/recording summary (no raw key / signed URL). */
export interface FeedbackMedia {
  kind: "image" | "video";
  /** True when object storage wasn't configured, so it's the "stored locally" note. */
  stored_locally: boolean;
  /** A freshly-presigned, expiring GET URL to open the original screenshot/recording (#1699). Present only when object storage holds the binary; omitted otherwise. */
  url?: string;
}

/**
 * The full detail shape from `GET /api/v1/feedback/<id>/`. Role-scoped exactly
 * like the list: admins get `description` + `github_issue_*` + `submitted_by_name`;
 * non-admins get `description` + `reference` ONLY (no outbound URL / submitter).
 * Both roles get the friendly `media` summary and the `technical` block.
 */
export interface FeedbackReportDetail {
  id: number;
  feedback_type: FeedbackType;
  title: string;
  description: string;
  status: FeedbackStatus;
  created_at: string;
  media: FeedbackMedia | null;
  technical: FeedbackTechnical;
  /** Agent view. */
  reference?: number;
  /**
   * Completion (close-out) layer (D4-FBK-018..021): both roles get these; the
   * modal renders the close-out summary + an outbound verify link.
   */
  is_done?: boolean;
  closeout_summary?: string;
  verify_url?: string;
  /** Admin-only. */
  github_issue_number?: number | null;
  github_issue_url?: string | null;
  submitted_by_name?: string;
  issue_state?: "" | "open" | "closed";
  issue_closed_at?: string | null;
  state_synced_at?: string | null;
  /** Admin-only triage block + whether this viewer may decide (#3830). */
  triage?: FeedbackTriage;
  can_approve?: boolean;
}

/**
 * Fetch one report's detail. The server decides role scoping: an agent
 * requesting another user's report (or a missing one) gets a 404.
 */
export async function getFeedbackReport(
  id: number,
  signal?: AbortSignal,
): Promise<FeedbackReportDetail> {
  // Refresh-aware (#1482): same stale-token recovery as the list so the detail
  // modal silently refreshes-and-retries instead of erroring on a 401.
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/${id}/`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (resp.status === 404) {
    throw new Error("That report could not be found.");
  }
  if (!resp.ok) {
    throw new Error(`Could not load this report (${resp.status}).`);
  }
  return resp.json();
}

// --- Completion / close-out layer (D4-FBK-018, D4-FBK-022) ------------------

/** Counts returned by the admin manual refresh, plus the refreshed roll-up. */
export interface FeedbackSyncResult {
  updated: number;
  unchanged: number;
  scanned: number;
  /** Rows that could not be refreshed this pass (#1449 partial-success). */
  skipped?: number;
  completion?: FeedbackCompletion;
  /** Non-alarming soft note when some rows were skipped (#1449). */
  note?: string;
}

/**
 * Admin-only "Refresh status": re-sync the linked GitHub issues for the given
 * report ids (the current page's rows, bounded server-side) and return the
 * refreshed counts + roll-up. Omitting `ids` lets the server pick a bounded
 * default set.
 */
export async function syncFeedbackIssueState(
  ids?: number[],
): Promise<FeedbackSyncResult> {
  // Refresh-aware (#1482): the admin "Refresh status" call recovers from a stale
  // access token the same way -- refresh once and retry the SAME POST.
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/sync/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ ids: ids ?? [] }),
    });
  });
  if (resp.status === 403) {
    throw new Error("You do not have permission to refresh report status.");
  }
  if (!resp.ok) {
    throw new Error(`Could not refresh report status (${resp.status}).`);
  }
  return resp.json();
}

/**
 * Admin-only: author/correct a report's plain-English close-out summary +
 * verify link. Stamps the close-out as manual server-side so the periodic sync
 * never overwrites it. Returns the refreshed detail.
 */
export async function updateFeedbackCloseout(
  id: number,
  patch: { closeout_summary?: string; verify_url?: string },
): Promise<FeedbackReportDetail> {
  // Refresh-aware (#1482): the admin close-out PATCH recovers from a stale access
  // token the same way -- refresh once and retry the SAME PATCH.
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/${id}/`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(patch),
    });
  });
  if (resp.status === 403 || resp.status === 404) {
    throw new Error("You do not have permission to edit this close-out.");
  }
  if (!resp.ok) {
    let detail = `Could not save the close-out (${resp.status}).`;
    try {
      const data = await resp.json();
      detail =
        (data?.detail as string) ||
        (data?.verify_url?.[0] as string) ||
        (data?.closeout_summary?.[0] as string) ||
        detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return resp.json();
}

// --- Triage approvals (#3830) ------------------------------------------------

export type FeedbackDecision = "approve" | "decline" | "comment";

/**
 * Approver-only: approve / decline a report awaiting approval, or leave a
 * comment. Server-enforced (only the configured approvers + superusers); the
 * decision and comment are posted back to the GitHub issue. Returns the
 * refreshed admin detail.
 */
export async function decideFeedback(
  id: number,
  decision: FeedbackDecision,
  comment = "",
): Promise<FeedbackReportDetail> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/${id}/decision/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ decision, comment }),
    });
  });
  if (!resp.ok) {
    let detail =
      resp.status === 403
        ? "Only an authorised approver can approve or decline."
        : `Could not save the decision (${resp.status}).`;
    try {
      const data = await resp.json();
      detail = (data?.detail as string) || detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return resp.json();
}

// --- Morning briefs (D4-FBK-038, #3920) -------------------------------------

export type FeedbackBriefStatus = "sent" | "withheld" | "skipped" | "failed";
export type FeedbackBriefGenerator = "" | "claude" | "fallback";

/** One row of the admin "Morning briefs" list (no e-mail body). */
export interface FeedbackBrief {
  id: number;
  digest_date: string;
  status: FeedbackBriefStatus;
  status_reason: string;
  subject: string;
  generator: FeedbackBriefGenerator;
  ai_model: string;
  ai_error: string;
  went_live: number;
  being_built: number;
  needs_you: number;
  requests_in: number;
  recipients_count: number;
  sent_count: number;
  error_message: string;
  last_run_at: string | null;
  created_at: string;
}

export interface FeedbackBriefSend {
  recipient: string;
  outcome: string;
  outcome_label: string;
  error_message: string;
  updated_at: string;
}

/** The exact e-mail that went out, plus each recipient's outcome. */
export interface FeedbackBriefDetail extends FeedbackBrief {
  html: string;
  text: string;
  ai_copy: Record<string, unknown> | null;
  sends: FeedbackBriefSend[];
}

export interface FeedbackBriefPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: FeedbackBrief[];
}

/** Admin-only list of every morning brief, newest first (403 for others). */
export async function listFeedbackBriefs(
  params: { page?: number; status?: FeedbackBriefStatus | "" } = {},
  signal?: AbortSignal,
): Promise<FeedbackBriefPage> {
  const qs = new URLSearchParams();
  if (params.page && params.page > 1) qs.set("page", String(params.page));
  if (params.status) qs.set("status", params.status);
  const query = qs.toString();
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/briefs/${query ? `?${query}` : ""}`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (resp.status === 403) {
    throw new Error("Only administrators can see the morning briefs.");
  }
  if (!resp.ok) {
    throw new Error(`Could not load the morning briefs (${resp.status}).`);
  }
  return resp.json();
}

/** One brief's exact HTML/text and per-recipient outcomes (admin-only). */
export async function getFeedbackBrief(
  id: number,
  signal?: AbortSignal,
): Promise<FeedbackBriefDetail> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/briefs/${id}/`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (resp.status === 404) {
    throw new Error("That brief could not be found.");
  }
  if (!resp.ok) {
    throw new Error(`Could not load this brief (${resp.status}).`);
  }
  return resp.json();
}

// --- Root cause (D4-FBK-039..041, #4200) -------------------------------------

/** A selectable tally period. */
export type FeedbackTallyPeriod =
  | "this_month"
  | "last_month"
  | "last_90_days"
  | "all";

/** Counts of reports per root cause for one period -- counts only, no names. */
export interface FeedbackRootCauseTally {
  period: FeedbackTallyPeriod;
  period_label: string;
  start: string | null;
  end: string | null;
  counts: {
    value: Exclude<FeedbackRootCause, "">;
    label: string;
    count: number;
  }[];
  unset: number;
  total: number;
  periods: { value: FeedbackTallyPeriod; label: string }[];
}

/** Admin-only root-cause tally for a period (403 for everyone else). */
export async function getFeedbackRootCauseTally(
  period: FeedbackTallyPeriod,
  signal?: AbortSignal,
): Promise<FeedbackRootCauseTally> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(
      `${API_BASE}/feedback/root-causes/?period=${encodeURIComponent(period)}`,
      { method: "GET", headers, ...(signal ? { signal } : {}) },
    );
  });
  if (!resp.ok) {
    throw new Error(`Could not load the root-cause tally (${resp.status}).`);
  }
  return resp.json();
}

/** Set or clear (`""`) one report's root cause (admin-only). */
export async function setFeedbackRootCause(
  id: number,
  rootCause: FeedbackRootCause,
): Promise<FeedbackReportDetail> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/${id}/root-cause/`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ root_cause: rootCause }),
    });
  });
  if (resp.status === 403) {
    throw new Error("Only administrators can set a report's root cause.");
  }
  if (!resp.ok) {
    let detail = `Could not save the root cause (${resp.status}).`;
    try {
      const data = await resp.json();
      detail = (data?.detail as string) || detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return resp.json();
}

// --- Daily curation (bulk approval of the day's changes) --------------------

export type CurationItemState =
  | "needs_decision"
  | "going_ahead"
  | "approved"
  | "declined"
  | "mixed"
  | "no_decision_needed";

export interface CurationCounts {
  new_today: number;
  open_total: number;
  work_items: number;
  needs_decision: number;
  going_ahead: number;
  /** Reports still awaiting a decision (what "Approve all" would change). */
  pending_reports?: number;
}

/** One row of the recent-curations picker. */
export interface CurationSummary {
  date: string;
  sent_at: string | null;
  counts: CurationCounts;
}

/**
 * One in-motion line: a curated work-item title (never the reporter's own
 * words), with how many reports it groups. `id` is its most recent report.
 */
export interface CurationMotionRow {
  id: number;
  issue_number: number | null;
  title: string;
  /** Reports grouped under this line (1 when not part of a work item). */
  reports?: number;
}

export interface CurationReport {
  id: number;
  issue_number: number | null;
  title: string;
  status: string;
  decision: string;
  submitted_by: string;
  self_authorised: boolean;
  pending: boolean;
}

export interface CurationItem {
  key: string;
  title: string;
  summary: string;
  impact: string;
  kind: "bug" | "change" | "data_fix" | string;
  state: CurationItemState;
  authoritative: boolean;
  reports: CurationReport[];
  /** Issue numbers in the item with no in-app report in this CRM. */
  unlinked_issues?: number[];
}

export interface CurationNotBug {
  summary: string;
  reason: string;
  reports: CurationReport[];
  unlinked_issues?: number[];
}

export interface Curation {
  date: string;
  digest: { summary: string; bullets: string[] } | null;
  counts: CurationCounts;
  in_motion: {
    live_recently: CurationMotionRow[];
    being_built: CurationMotionRow[];
    approved_waiting: CurationMotionRow[];
    /** True counts; each list above is capped. */
    totals?: { live_recently: number; being_built: number; approved_waiting: number };
  };
  can_decide: boolean;
  items: CurationItem[];
  not_bugs: CurationNotBug[];
}

export interface CurationDecideInput {
  decision: "approved" | "declined";
  item_keys?: string[];
  report_ids?: number[];
  comment?: string;
}

export interface CurationDecideResult {
  changed: number[];
  skipped: { id: number; reason: string }[];
}

/** The most recent curations, newest first (admin-only). */
export async function listFeedbackCurations(
  signal?: AbortSignal,
): Promise<CurationSummary[]> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/curations/`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (resp.status === 403) {
    throw new Error("Only administrators can see the daily curation.");
  }
  if (!resp.ok) {
    throw new Error(`Could not load the recent curations (${resp.status}).`);
  }
  const data = await resp.json();
  // Tolerate a paginated envelope as well as a bare list.
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * One day's curation (`date` = "YYYY-MM-DD" or "latest"). Resolves `null` when
 * no curation exists for that day (404), so the page can show an empty state.
 */
export async function getFeedbackCuration(
  date: string,
  signal?: AbortSignal,
): Promise<Curation | null> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/feedback/curations/${encodeURIComponent(date)}/`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  });
  if (resp.status === 404) return null;
  if (resp.status === 403) {
    throw new Error("Only administrators can see the daily curation.");
  }
  if (!resp.ok) {
    throw new Error(`Could not load the day's curation (${resp.status}).`);
  }
  return resp.json();
}

/**
 * Approve or decline reports in one day's curation. With no `item_keys` and no
 * `report_ids` the server applies the decision to every report in the batch
 * still awaiting a decision ("Approve all").
 */
export async function decideFeedbackCuration(
  date: string,
  input: CurationDecideInput,
): Promise<CurationDecideResult> {
  const resp = await authedFeedbackFetch((token) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(
      `${API_BASE}/feedback/curations/${encodeURIComponent(date)}/decide/`,
      { method: "POST", headers, body: JSON.stringify(input) },
    );
  });
  if (!resp.ok) {
    let detail =
      resp.status === 403
        ? "Only an approver can decide on these changes."
        : `Could not save your decision (${resp.status}).`;
    try {
      const data = await resp.json();
      detail = (data?.detail as string) || detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  const data = await resp.json();
  return { changed: data?.changed ?? [], skipped: data?.skipped ?? [] };
}
